import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { User, Mail, Lock, Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle, Building2, BadgeCheck } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { PENDING_ORG_INVITE_TOKEN_KEY } from '../constants/orgInvite'

function SignupPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [signupRoleBand, setSignupRoleBand] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { signup } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const t = searchParams.get('token')?.trim()
    if (t) {
      try {
        localStorage.setItem(PENDING_ORG_INVITE_TOKEN_KEY, t)
      } catch {
        /* ignore */
      }
    }
  }, [searchParams])

  const passwordChecks = [
    { label: 'At least 8 characters', check: password.length >= 8 },
    { label: 'Contains a number', check: /\d/.test(password) },
    { label: 'Contains uppercase letter', check: /[A-Z]/.test(password) },
  ]

  const isPasswordValid = passwordChecks.every((c) => c.check)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!isPasswordValid) {
      setError('Please meet all password requirements')
      return
    }

    if (!companyName.trim() || !jobTitle.trim() || !signupRoleBand) {
      setError('Please add your company, title, and role.')
      return
    }

    setLoading(true)

    try {
      await signup(email, password, name, {
        company_name: companyName.trim(),
        job_title: jobTitle.trim(),
        signup_role_band: signupRoleBand,
      })
      navigate('/verify-email', { replace: true })
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-primary-50 to-accent-50 p-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Transform Your Team's Meetings
          </h2>
          <div className="space-y-6">
            {[
              { title: 'Balance Participation', desc: 'Create more balanced and collaborative discussions' },
              { title: 'Stay Focused', desc: 'Automatically keep discussions aligned to the agenda' },
              { title: 'Finish Faster', desc: 'Help meetings stay efficient and on schedule' },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  <p className="text-gray-500">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12">
            <div className="w-12 h-10 flex items-center justify-center">
              <img src="/rithmo-mark.png" alt="Rithmo logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-gray-900">Rithmo</span>
          </Link>

          {/* Header */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create your account</h1>
          <p className="text-gray-500 mb-8">Create your account to get started. No credit card required.</p>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 mb-6"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Input */}
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="input-field pl-12"
                required
              />
            </div>

            {/* Email Input */}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Work email"
                className="input-field pl-12"
                required
              />
            </div>

            <div className="relative">
              <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Company name"
                className="input-field pl-12"
                required
              />
            </div>

            <div className="relative">
              <BadgeCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Job title"
                className="input-field pl-12"
                required
              />
            </div>

            <div>
              <label htmlFor="signup-role-band" className="block text-sm font-medium text-gray-700 mb-2">
                Which best describes your role at your company?
              </label>
              <select
                id="signup-role-band"
                value={signupRoleBand}
                onChange={(e) => setSignupRoleBand(e.target.value)}
                className="input-field w-full"
                required
              >
                <option value="">Select one</option>
                <option value="senior_leadership">Senior leadership</option>
                <option value="middle_management">Manager / team lead</option>
                <option value="individual_other">Individual contributor / exploring</option>
              </select>
            </div>

            {/* Password Input */}
            <div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create password"
                  className="input-field pl-12 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Password Requirements */}
              {password && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 space-y-2"
                >
                  {passwordChecks.map((check) => (
                    <div
                      key={check.label}
                      className={`flex items-center gap-2 text-sm ${
                        check.check ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      <CheckCircle className={`w-4 h-4 ${check.check ? '' : 'opacity-50'}`} />
                      <span>{check.label}</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Terms */}
            <p className="text-sm text-gray-400">
              By signing up, you agree to our{' '}
              <Link to="/terms" className="text-primary-600 hover:text-primary-700">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700">Privacy Policy</Link>.
            </p>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={
                loading
                || !isPasswordValid
                || !(name ?? '').trim()
                || !(email ?? '').trim()
                || !(companyName ?? '').trim()
                || !(jobTitle ?? '').trim()
                || !signupRoleBand
              }
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Sign In Link */}
          <p className="text-center text-gray-500 mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  )
}

export default SignupPage
